// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, Station } from '../types';
import { stripes } from '../charts';
import { anomalyLegend } from '../color';
import { fmtRate, esc, STATE_NAMES } from '../format';
import { gloss } from '../glossary';
import { openDrawer } from '../drawer';
import { sectionHead, legendHtml } from './ui';

type Sort = 'rate' | 'name' | 'state';

export function mount(el: HTMLElement, data: ClimateData): void {
  const withBase = data.stations.filter((s) => s.hasBaseline);
  const states = ['ALL', ...Array.from(new Set(data.stations.map((s) => s.state))).sort()];
  let sort: Sort = (localStorage.getItem('warm.stripes.sort') as Sort) || 'rate';
  let state = localStorage.getItem('warm.stripes.state') || 'ALL';

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('Warming stripes for every station', `One row per ${gloss('station', 'reference station')}, each band a year coloured by its ${gloss('anomaly', 'anomaly')} against the ${gloss('baseline', '1961–1990 average')}. Read top-to-bottom to compare places; each strip goes blue→red as it warms. Click a strip to open its full record.`)}
      <div class="controls">
        <label>State
          <select data-state>${states.map((s) => `<option value="${s}" ${s === state ? 'selected' : ''}>${s === 'ALL' ? 'All Australia' : STATE_NAMES[s] ?? s}</option>`).join('')}</select>
        </label>
        <label>Sort
          <select data-sort>
            <option value="rate" ${sort === 'rate' ? 'selected' : ''}>Fastest warming</option>
            <option value="name" ${sort === 'name' ? 'selected' : ''}>Name (A–Z)</option>
            <option value="state" ${sort === 'state' ? 'selected' : ''}>State</option>
          </select>
        </label>
      </div>
      ${legendHtml('Cooler', 'Warmer', anomalyLegend(1.6))}
      <div class="stripe-grid" data-grid></div>
      <p class="muted note">${data.stations.length - withBase.length} station(s) lack a full 1961–1990 baseline and are omitted from this view; find them in the Explorer.</p>
    </section>`;

  const grid = el.querySelector('[data-grid]') as HTMLElement;

  const render = () => {
    let rows = state === 'ALL' ? withBase.slice() : withBase.filter((s) => s.state === state);
    rows.sort(sorter(sort));
    grid.innerHTML = rows.map((s) => stripeRow(s)).join('');
  };

  el.querySelector('[data-state]')?.addEventListener('change', (e) => {
    state = (e.target as HTMLSelectElement).value; localStorage.setItem('warm.stripes.state', state); render();
  });
  el.querySelector('[data-sort]')?.addEventListener('change', (e) => {
    sort = (e.target as HTMLSelectElement).value as Sort; localStorage.setItem('warm.stripes.sort', sort); render();
  });
  grid.addEventListener('click', (e) => {
    const r = (e.target as Element).closest('[data-station]') as HTMLElement | null;
    if (r) openDrawer(data, r.getAttribute('data-station')!);
  });

  render();
}

function sorter(sort: Sort): (a: Station, b: Station) => number {
  if (sort === 'name') return (a, b) => a.name.localeCompare(b.name);
  if (sort === 'state') return (a, b) => a.state.localeCompare(b.state) || (b.rateMean ?? 0) - (a.rateMean ?? 0);
  return (a, b) => (b.rateMean ?? -9) - (a.rateMean ?? -9);
}

function stripeRow(s: Station): string {
  const svg = stripes(s.annual.map((r) => ({ y: r.y, a: r.aMean })), { w: 600, h: 30 });
  return `<button class="strip-row" data-station="${s.id}" title="${esc(s.name)}">
    <span class="strip-name">${esc(s.name)}<span class="strip-state">${s.state}</span></span>
    <span class="strip-svg">${svg}</span>
    <span class="strip-rate">${fmtRate(s.rateMean)}</span>
  </button>`;
}
